import type { DocPage } from "../types";

export const page: DocPage = {
  slug: "trading",
  title: "Trade proposals and orders",
  navLabel: "Trading",
  description:
    "Move from an Agent-drafted trade proposal to an explicit approval, account checks, broker or paper execution, and a recorded outcome.",
  section: "portfolio-trading",
  published: "2026-08-03",
  updated: "2026-08-03",
  targets: ["Mavori trade proposal", "approve an AI trading order", "track a Mavori order"],
  related: ["agent", "accounts", "portfolio", "activity"],
  blocks: [
    {
      type: "lead",
      text: "The Agent can prepare a trade proposal for a selected Portfolio Account, but the proposal is not an order. Mavori pauses at an approval card so you can inspect the exact action before anything is sent for execution.",
    },

    { type: "h2", text: "Prepare the proposal" },
    {
      type: "p",
      text: "Select the relevant account in the chat composer, then ask the [Agent](/docs/agent) to evaluate or draft the trade. A useful request names the security, intended direction, sizing idea, and any price condition. The Agent can use the selected account's holdings, cash, and Risk Controls when preparing the ticket.",
    },
    {
      type: "p",
      text: "If the account is not eligible for the requested action, the proposal flow explains the setup or account constraint instead of treating a draft as executable. Read access to an account does not grant trading access.",
    },

    { type: "h2", text: "Set Risk Controls on the account" },
    {
      type: "p",
      text: "Risk Controls live on each Portfolio Account. Open the account in **Portfolio**, configure the limits and review requirements that fit it, and activate the controls you want enforced. Each control states whether it blocks an order, requires review, or monitors account state. Review them again when the account, Strategy, or intended exposure changes.",
    },

    { type: "h2", text: "Review the approval card" },
    {
      type: "ul",
      items: [
        "Confirm the Portfolio Account and whether it is Live or Paper.",
        "Check the security, buy or sell side, quantity, and order type.",
        "Review any limit or stop price, time in force, and estimated exposure shown.",
        "Read the rationale and the result of applicable Risk Controls.",
        "Choose **Approve order** or **Reject**. An unanswered or expired card places nothing.",
      ],
    },

    { type: "h2", text: "Understand the execution boundary" },
    {
      type: "p",
      text: "Approval authorizes the specific ticket displayed on the card. It does not authorize later trades or a different account. Before a live order proceeds, the connection and account permissions must allow trading and active Risk Controls may block or require review of the request.",
    },
    {
      type: "callout",
      variant: "warning",
      title: "Approval is not a fill",
      text: "A submitted order can fill completely, fill partially, remain open, expire, or be rejected. Market conditions and broker handling can change the final price and quantity. Follow the order until it reaches a state you understand.",
    },

    { type: "h2", text: "Track the outcome" },
    {
      type: "p",
      text: "Open **Portfolio → Trade Logs** to follow the order's account, status, fill quantity, average fill price, fees, source, and linked thesis. Use status filters to focus on open, filled, canceled, or rejected activity. When a status is uncertain, verify the order at the connected broker before taking another action.",
    },
    {
      type: "p",
      text: "Paper orders follow the same review flow but settle in the Paper Account. Trades made elsewhere can be recorded manually in an eligible Manual Account. See [Trade Logs and Journal](/docs/activity) for reviewing what happened and why.",
    },
  ],
};
